import React from 'react';
import axios from 'axios';
import { rollbar } from '../Rollbar.js';

export class AddonSelection extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            addons: [],
            selectedIds: [],
            wantToPort: false,
            voipPhone: '',
            phoneError: false,
            loading: true,
            error: false
        };

        this.handleToggle = this.handleToggle.bind(this);
        this.handleWantToPortChange = this.handleWantToPortChange.bind(this);
        this.handlePhoneChange = this.handlePhoneChange.bind(this);
        this.handleContinue = this.handleContinue.bind(this);
    }

    async componentDidMount() {
        document.getElementById("addonSelection")?.scrollIntoView({ block: 'start', behavior: 'smooth' });

        const { locationId, tariffId, routerId } = this.props;
        const url = `${process.env.REACT_APP_THIRTEEN_TEN_API}/addons?locationId=${locationId}&tariffId=${tariffId}&routerId=${routerId ?? 0}`;
        console.log('[AddonSelection] fetching', url);

        await axios.get(url)
            .then(response => {
                console.log('[AddonSelection] response', response.data);
                this.setState({ addons: response.data });
            })
            .catch(error => {
                console.error('[AddonSelection] error', error?.response?.status, error?.response?.data);
                rollbar.error(error);
                this.setState({ error: true });
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    }

    hasVoipSelected() {
        return this.state.addons.some(
            a => this.state.selectedIds.includes(a.productId) && a.addonType === 'voip'
        );
    }

    handleToggle(productId) {
        const addon = this.state.addons.find(a => a.productId === productId);
        this.setState(prev => {
            const already = prev.selectedIds.includes(productId);
            if (already) {
                return { selectedIds: prev.selectedIds.filter(id => id !== productId), phoneError: false };
            }
            // Only one VoIP service can be selected at a time — deselect any existing VoIP
            let ids = [...prev.selectedIds];
            if (addon && addon.addonType === 'voip') {
                const voipIds = this.state.addons.filter(a => a.addonType === 'voip').map(a => a.productId);
                ids = ids.filter(id => !voipIds.includes(id));
            }
            return { selectedIds: [...ids, productId], phoneError: false };
        });
    }

    handleWantToPortChange(e) {
        this.setState({ wantToPort: e.target.checked, voipPhone: '', phoneError: false });
    }

    handlePhoneChange(e) {
        this.setState({ voipPhone: e.target.value, phoneError: false });
    }

    // Strip all non-digits then validate as 11-digit UK geographic landline (01xxx/02xxx) or 12-digit international equivalent (441xxx/442xxx)
    isValidUkLandline(number) {
        const digits = number.replace(/\D/g, '');
        return (digits.length === 11 && (digits.startsWith('01') || digits.startsWith('02'))) ||
               (digits.length === 12 && (digits.startsWith('441') || digits.startsWith('442')));
    }

    handleContinue() {
        if (this.hasVoipSelected() && this.state.wantToPort) {
            const phone = this.state.voipPhone.trim();
            if (!this.isValidUkLandline(phone)) {
                this.setState({ phoneError: true });
                return;
            }
        }

        const byType = type => this.state.addons
            .filter(a => this.state.selectedIds.includes(a.productId) && a.addonType === type)
            .map(a => a.productId);

        const voipPhone = this.hasVoipSelected() && this.state.wantToPort
            ? this.state.voipPhone.trim()
            : '00000000000';

        this.props.onContinue(byType('product'), byType('voip'), byType('recurring'), voipPhone);
    }

    render() {
        if (this.state.loading) {
            return (
                <div id="addonSelection" className="App m-5">
                    <div className="fa-2x">
                        <i className="fas fa-spinner fa-pulse"></i>
                    </div>
                </div>
            );
        }

        if (this.state.error || this.state.addons.length === 0) {
            return (
                <div id="addonSelection" className="App m-5">
                    <h1 className="title is-3">Extras</h1>
                    <p className="block">No extras available at this time.</p>
                    <button className="button is-primary" onClick={this.handleContinue}>Continue</button>
                </div>
            );
        }

        const voipSelected = this.hasVoipSelected();

        const selectedAddons = this.state.addons.filter(a => this.state.selectedIds.includes(a.productId));
        const router = this.props.selectedRouter;
        const product = this.props.selectedProduct;
        const upfrontItems = [
            ...(product && product.setupFee > 0 ? [{ name: 'Connection fee', price: product.setupFee }] : []),
            ...(router === null
                ? [{ name: 'Wires only', price: null }]
                : router && router.price > 0 ? [{ name: router.name, price: router.price }] : []),
            ...selectedAddons.filter(a => !a.isMonthly).map(a => ({ name: a.name, price: a.priceIncVat }))
        ];
        const monthlyItems = [
            ...(product && product.monthlyFee > 0 ? [{ name: product.productName, price: product.monthlyFee }] : []),
            ...selectedAddons.filter(a => a.isMonthly).map(a => ({ name: a.name, price: a.priceIncVat }))
        ];

        return (
            <div id="addonSelection" className="App m-5">
                <h1 className="title is-3">Extras</h1>
                <p className="block">
                    Would you like to add any of the following to your order?
                </p>
                <h2 className="title is-6">All prices include VAT</h2>
                <div className="columns is-multiline is-mobile is-centered">
                    {this.state.addons.map(addon => {
                        const selected = this.state.selectedIds.includes(addon.productId);
                        const unavailable = !addon.inStock;
                        return (
                            <div key={addon.productId} className="column is-full-mobile is-two-thirds-tablet is-half-desktop is-one-third-widescreen is-one-quarter-fullhd">
                                <div
                                    className={`card mb-3 ${unavailable ? 'unavailable' : selected ? 'selected' : 'hover'}`}
                                    onClick={() => { if (!unavailable) this.handleToggle(addon.productId); }}
                                    style={{ cursor: unavailable ? 'default' : 'pointer' }}
                                >
                                    <div className="card-header">
                                        <div className="card-header-title is-centered">
                                            <h2 className="subtitle is-5">{addon.name}</h2>
                                        </div>
                                    </div>
                                    <div className="card-content product-description">
                                        {addon.description && <p className="pre mb-5">{addon.description}</p>}
                                        <p>£{addon.priceIncVat.toFixed(2)}{addon.isMonthly ? '/month' : ''}</p>
                                        <div className="field mt-5">
                                            <div className="control">
                                                <button
                                                    className={`button ${selected ? 'is-success' : 'is-primary'}`}
                                                    onClick={e => { e.stopPropagation(); if (!unavailable) this.handleToggle(addon.productId); }}
                                                    disabled={unavailable}
                                                >
                                                    {unavailable ? 'Out of stock' : selected ? 'Remove' : 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {voipSelected && (
                    <div className="block">
                        <div className="field">
                            <label className="checkbox">
                                <input
                                    type="checkbox"
                                    checked={this.state.wantToPort}
                                    onChange={this.handleWantToPortChange}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                I have an existing number I'd like to keep (number port)
                            </label>
                        </div>
                        {this.state.wantToPort ? (
                            <div className="field mt-3">
                                <label className="label">Your existing phone number</label>
                                <div className="control">
                                    <input
                                        className={`input${this.state.phoneError ? ' is-danger' : ''}`}
                                        type="tel"
                                        placeholder="e.g. 01234 567890 or 020 7946 0000"
                                        value={this.state.voipPhone}
                                        onChange={this.handlePhoneChange}
                                    />
                                </div>
                                {this.state.phoneError && (
                                    <p className="help is-danger">Please enter a valid UK landline number (starting 01 or 02).</p>
                                )}
                            </div>
                        ) : (
                            <p className="help mt-2">We will allocate a new phone number for you.</p>
                        )}
                    </div>
                )}
                {(upfrontItems.length > 0 || monthlyItems.length > 0) && (
                    <div className="box has-background-light mt-5" style={{ maxWidth: '420px', margin: '1.5rem auto 0' }}>
                        <p className="has-text-weight-semibold is-size-6 mb-3">Your internet package summary</p>
                        {upfrontItems.length > 0 && (
                            <div className={monthlyItems.length > 0 ? 'mb-4' : ''}>
                                <p className="has-text-grey is-size-7 mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>One-off</p>
                                {upfrontItems.map((item, i) => (
                                    <div key={i} className="is-flex is-justify-content-space-between is-size-6 mb-1">
                                        <span>{item.name}</span>
                                        <span className="ml-5 has-text-weight-semibold">{item.price !== null ? `£${item.price.toFixed(2)}` : 'Included'}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {monthlyItems.length > 0 && (
                            <div>
                                <p className="has-text-grey is-size-7 mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Monthly</p>
                                {monthlyItems.map((item, i) => (
                                    <div key={i} className="is-flex is-justify-content-space-between is-size-6 mb-1">
                                        <span>{item.name}</span>
                                        <span className="ml-5 has-text-weight-semibold">£{item.price.toFixed(2)}<span className="has-text-grey is-size-7">/mo</span></span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className="block mt-4">
                    <button className="button is-primary" onClick={this.handleContinue}>
                        {this.state.selectedIds.length > 0 ? 'Continue with extras' : 'Continue without extras'}
                    </button>
                </div>
            </div>
        );
    }
}
